import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { AccountColor, FieldDef } from 'app/core/models/account.types';
import { ChartPanelMeta, PanelComponentBase } from 'app/shared/components/panels/panels.types';
import { FormBuilder, Validators } from '@angular/forms';
import { TimeSeriesChartService } from 'app/shared/modules/time-series-charts/time-series-charts.service';
import moment from 'moment';
import { RelatedToType } from '@shared/enums';
import { AuthService } from '@core/services';
import { NewPermission } from '@core/enums/permission';
import { Project } from 'app/modules/projects/projects.types';
import { cloneDeep } from 'lodash-es';
import { Select } from '@ngxs/store';
import { AccountState } from '@state/account/state';

@Component({
  selector: 'project-panel-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsChartComponent extends PanelComponentBase<ChartPanelMeta> implements OnInit {
  panelName = 'Chart Panel';
  timeSeriesFields$: Observable<FieldDef[]>;
  chart: any;
  charts$: Observable<any[]>;
  chartIdSelected: number;
  chartForm;
  chartPanelOptions;
  NewPermission = NewPermission;
  panelColor: any = null;
  titleFieldDef = {
    name: 'title',
    label: 'Title',
    type: 'text',
  };
  project: Project;

  @Select(AccountState.selectAccountColorsWithNone) protected readonly accountColors$: Observable<AccountColor[]>;

  constructor(
    private readonly timeSeriesChartService: TimeSeriesChartService,
    private readonly fb: FormBuilder,
    private authService: AuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.project = cloneDeep(this.model);
    this.timeSeriesChartService.relatedToId = this.model.id;
    this.timeSeriesChartService.relatedToType = RelatedToType.Project;
    this.charts$ = this.timeSeriesChartService
      .getTimeSeriesCharts({
        accountId: Number(this.data.account.id),
        relatedToId: this.model.id,
        relatedToType: RelatedToType.Project,
      })
      .pipe(
        tap((data) => {
          this.chart = data.time_series_charts.find((c) => c.id == this.panelDef.meta.chartId);
        }),
        map((data) => data.time_series_charts),
      );
    this.ui = { ...this.ui, editMode: false };
    this.panelDefMemento = JSON.parse(JSON.stringify(this.panelDef));
    this.timeSeriesFields$ = this.timeSeriesChartService.getTimeSeriesFields();
    this.panelName = this.panelDef.title;
    this.titleFieldDef.label = this.panelDef.title;
    this.model = this.panelDef;
    this.chartForm = this.fb.group({
      chartId: [this.panelDef.meta.chartId, [Validators.required]],
      activeTab: [this.panelDef.meta.activeTab, [Validators.required]],
      startDate: [this.panelDef.meta.startDate || moment().subtract(365, 'days'), [Validators.required]],
      endDate: [this.panelDef.meta.endDate || moment().add(365, 'days'), [Validators.required]],
    });

    this.chartPanelOptions = this.chartForm.value;
  }

  selectHeaderColor(color: string): void {
    if (color?.toLowerCase() === 'none') {
      this.panelColor = null;
    } else {
      this.panelColor = color;
    }

    this.saveHeaderColor();
  }

  private saveHeaderColor(): void {
    this.panelDef = {
      ...this.panelDef,
      color: this.panelColor,
    };

    this.panelDefUpdated.emit(this.panelDef);
  }

  update(params: any): void {
    let currentKey = Object.keys(params)[0];
    if (currentKey === this.titleFieldDef.name) {
      this.panelName = params[this.titleFieldDef.name];
    }
  }

  toggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.editToggled.emit({ toggle: true, panelDef: this.panelDef });
  }

  cancelEdits(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.panelDef = JSON.parse(JSON.stringify(this.panelDefMemento));
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
  }

  saveLayout(): void {
    this.chartForm.markAllAsTouched();
    this.chartForm.updateValueAndValidity();
    if (this.chartForm.valid) {
      this.panelDef = {
        ...this.panelDef,
        title: this.panelName,
        meta: this.chartForm.value,
      };

      this.panelDefUpdated.emit(this.panelDef);
      this.ui = { ...this.ui, editMode: !this.ui.editMode };
      this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
    }
  }

  canEditDeal(): [NewPermission, boolean] {
    return [NewPermission.DEALS_EDIT, this.project.permissions?.is_editable];
  }
}
